/**
 * Money: integer cents (lesson 8), branded so a quantity or a float can't be passed by
 * accident. One formatter for the whole product: `formatMoney`.
 * @module
 */
import type Decimal from 'decimal.js';
import { z } from 'zod';
import { NUMBER_STYLE, formatDecimalParts } from '../format/index.js';
import { D, type RoundingMode, decimalRounding } from '../internal/decimal.js';
import { type QtyInput, qty } from '../quantity/index.js';

declare const centsBrand: unique symbol;

/** An amount of money in integer cents (ZAR in V1). Always a safe integer. */
export type Cents = number & { readonly [centsBrand]: true };

/** Wraps an integer as {@link Cents}. Throws `RangeError` unless it is a safe integer. */
export function cents(value: number): Cents {
  if (!Number.isSafeInteger(value)) {
    throw new RangeError(`Cents must be a safe integer, got ${value}`);
  }
  return (value === 0 ? 0 : value) as Cents; // normalise −0
}

/** True when `value` is a valid cents amount. */
export function isCents(value: unknown): value is Cents {
  return typeof value === 'number' && Number.isSafeInteger(value);
}

/** Zero cents. */
export const ZERO_CENTS: Cents = cents(0);

/** a + b. Throws if the result leaves the safe-integer range. */
export function addCents(a: Cents, b: Cents): Cents {
  return cents(a + b);
}

/** a − b. */
export function subCents(a: Cents, b: Cents): Cents {
  return cents(a - b);
}

/** −a. */
export function negCents(a: Cents): Cents {
  return cents(-a);
}

/** Sum of many amounts. Empty → 0. */
export function sumCents(values: Iterable<Cents>): Cents {
  let total = 0;
  for (const v of values) total = cents(total + cents(v));
  return cents(total);
}

function toCents(d: Decimal, mode: RoundingMode): Cents {
  return cents(d.toDecimalPlaces(0, decimalRounding(mode)).toNumber());
}

/**
 * Unit price × quantity, rounded to whole cents (default `half_up`: ties away from zero).
 * `mulQty(cents(1999), '0.25')` → 500 (499.75 rounds up).
 */
export function mulQty(price: Cents, quantity: QtyInput, mode: RoundingMode = 'half_up'): Cents {
  return toCents(new D(cents(price)).times(qty(quantity)), mode);
}

/**
 * Splits `total` across `weights` so the parts **always sum exactly to `total`**.
 * Each part is first rounded with `mode`; any cents left over (or overshot) are then
 * given to (or taken from) the parts whose rounding moved them furthest. Ties favour
 * earlier parts (they get the extra cent, later parts give one back). Works for negative
 * totals (refunds) as the exact mirror of the positive split. Weights are quantities ≥ 0
 * with a positive sum.
 *
 * `allocate(cents(1000), ['1','1','1'])` → [334, 333, 333].
 */
export function allocate(
  total: Cents,
  weights: readonly QtyInput[],
  mode: RoundingMode = 'half_up',
): Cents[] {
  if (cents(total) < 0) return allocate(negCents(total), weights, mode).map(negCents);
  if (weights.length === 0) throw new RangeError('allocate needs at least one weight');
  const ws = weights.map((w) => qty(w));
  if (ws.some((w) => w.isNegative() && !w.isZero())) {
    throw new RangeError('allocate weights must be >= 0');
  }
  const weightSum = ws.reduce((acc, w) => acc.plus(w), new D(0));
  if (weightSum.isZero()) throw new RangeError('allocate weights must not all be zero');

  const exact = ws.map((w) => new D(total).times(w).div(weightSum));
  const parts = exact.map((e) => toCents(e, mode) as number);
  let residual = total - parts.reduce((a, b) => a + b, 0);
  if (residual !== 0) {
    const step = residual > 0 ? 1 : -1;
    // Error = exact − rounded; adjust the parts rounding moved furthest first. On ties,
    // extra cents go to earlier parts and clawed-back cents come from later parts, so
    // the result is the same whatever the rounding mode.
    const order = exact
      .map((e, i) => ({ i, err: e.minus(parts[i] as number).times(step) }))
      .sort((a, b) => b.err.comparedTo(a.err) || (a.i - b.i) * step);
    for (let k = 0; residual !== 0; k = (k + 1) % order.length) {
      const idx = (order[k] as { i: number }).i;
      parts[idx] = (parts[idx] as number) + step;
      residual -= step;
    }
  }
  return parts.map(cents);
}

/**
 * Parses a user-typed rand amount into cents: `"12.50"`, `"12,5"`, `"R 1 234,50"`,
 * `"1,234.50"`, `"-3"`, and anything {@link formatMoney} prints (`"−R 99.00"`, with a
 * true minus). Up to two decimal places, `.` or `,` as the decimal separator. Spaces
 * group thousands; `.`/`,` grouping is accepted only together with a decimal part using
 * the other separator, because `"1,234"` alone is ambiguous. Throws `RangeError` on
 * anything else.
 */
export function parseMoney(text: string): Cents {
  const cleaned = text
    .replace(/[\s\u00a0\u202f]/g, '')
    .replace(/^\u2212/, '-')
    .replace(/^(-?)R/i, '$1');
  const m =
    /^(-?)(\d+)(?:[.,](\d{1,2}))?$/.exec(cleaned) ??
    /^(-?)(\d{1,3}(?:,\d{3})+)\.(\d{1,2})$/.exec(cleaned) ??
    /^(-?)(\d{1,3}(?:\.\d{3})+),(\d{1,2})$/.exec(cleaned);
  if (!m) throw new RangeError(`Not a money amount: ${JSON.stringify(text)}`);
  const sign = m[1] === '-' ? -1 : 1;
  const whole = (m[2] as string).replace(/[.,]/g, '');
  const frac = (m[3] ?? '').padEnd(2, '0');
  return cents(sign * Number(BigInt(whole) * 100n + BigInt(frac)));
}

/** Cents as a plain decimal rand string: 123450 → `"1234.50"`, −5 → `"-0.05"`. */
export function centsToDecimalString(amount: Cents): string {
  const v = cents(amount);
  const abs = Math.abs(v);
  const rand = Math.trunc(abs / 100);
  const c = abs % 100;
  return `${v < 0 ? '-' : ''}${rand}.${c.toString().padStart(2, '0')}`;
}

/**
 * The one money formatter: rand in the product's number style (D-022, `NUMBER_STYLE`).
 * `formatMoney(cents(1248000))` → `R 12 480.00` and −9900 → `−R 99.00` (a true minus,
 * U+2212). The spaces are **non-breaking** (U+00A0), so a price never wraps. Formats from
 * a decimal string, never through a float.
 */
export function formatMoney(amount: Cents): string {
  const { negative, digits } = formatDecimalParts(centsToDecimalString(amount));
  return `${negative ? NUMBER_STYLE.minus : ''}R\u00a0${digits}`;
}

const wireCents = z
  .number()
  .int()
  .refine((n) => Number.isSafeInteger(n), 'Must be a safe integer');

/**
 * zod schema for cents on the wire: a safe integer, parsed to {@link Cents}. A codec (not a
 * one-way transform), so a response schema can encode it back: API responses that carry
 * cents, e.g. an import rules document with a `set_cost` decision, serialise.
 */
export const centsSchema = z.codec(
  wireCents,
  z.custom<Cents>((v) => typeof v === 'number' && Number.isSafeInteger(v)),
  {
    decode: (n) => cents(n),
    encode: (c) => c,
  },
);
